/**
 * The twenty-five stations, as the authoring assistant sees them.
 *
 * A thin view over `fab/layout.ts`, and thin on purpose: the catalogue is
 * that file's, the names are the i18n catalogue's, and nothing here is a
 * second list that could drift from either. What this module adds is the two
 * orderings the assistant needs and layout.ts has no reason to state.
 *
 * FLOW ORDER IS AISLE ORDER, AND IT IS NOT ARRAY ORDER. `TOOLS` is written
 * roughly in flow order but not exactly - THRM-04 is declared eleventh and
 * stands in bay 02, because a furnace is used at several points in a route
 * and was placed where the wafer first meets it. The order a wafer meets the
 * tools is the order of the AISLE, which is authored x. So that is what this
 * sorts by, and it is the order a new floor is laid out in unless its author
 * says otherwise.
 */

import { AUTHORED_TOOL_X, CATALOGUE, toolName, toolProcess, type FlowZone, type Tool } from '../../fab/layout'

export type { FlowZone, Tool }

/** Every station, whether or not it is standing on the floor right now. */
export const STATIONS: readonly Tool[] = CATALOGUE

const byId = new Map(STATIONS.map((s) => [s.id, s]))

export const station = (id: string): Tool | null => byId.get(id) ?? null

export const stationIds = (): string[] => STATIONS.map((s) => s.id)

export const tagOf = (id: string): string => byId.get(id)?.assetTag ?? id

export const idOfTag = (tag: string): string | null =>
  STATIONS.find((s) => s.assetTag === tag)?.id ?? null

/**
 * Position in the flow, by the aisle of the authored hall. Tools sharing a
 * bay are ordered with the -z face first, which is bay 01's first face and
 * so the one a wafer meets first walking in.
 */
const FLOW_INDEX: ReadonlyMap<string, number> = new Map(
  [...STATIONS]
    .sort((a, b) => {
      const ax = AUTHORED_TOOL_X.get(a.assetTag) ?? 0
      const bx = AUTHORED_TOOL_X.get(b.assetTag) ?? 0
      return ax - bx || a.position[2] - b.position[2]
    })
    .map((s, i) => [s.id, i]),
)

export const flowIndex = (id: string): number => FLOW_INDEX.get(id) ?? Number.MAX_SAFE_INTEGER

/** The ids in flow order - the order a wafer meets them. */
export const FLOW_ORDER: readonly string[] = [...FLOW_INDEX.entries()]
  .sort((a, b) => a[1] - b[1])
  .map(([id]) => id)

/** Put a set of ids into flow order. Used whenever a floor is built from a description. */
export const inFlowOrder = (ids: Iterable<string>): string[] =>
  [...new Set(ids)].sort((a, b) => flowIndex(a) - flowIndex(b))

/**
 * The zones, in the order the aisle runs them. The assistant answers "add
 * the whole front end" from this, and the dialog groups the palette by it.
 */
export const ZONES: readonly FlowZone[] = [
  'entry',
  'substrate',
  'frontend',
  'metrology',
  'interconnect',
  'sort',
  'assembly',
  'support',
]

export const idsInZone = (zone: FlowZone): string[] =>
  inFlowOrder(STATIONS.filter((s) => s.zone === zone).map((s) => s.id))

/** What the hardware is called, in the reader's language. */
export const nameOf = (id: string): string => {
  const s = byId.get(id)
  return s ? toolName(s) : id
}

/** What it does to a wafer, in the reader's language. */
export const processOf = (id: string): string => {
  const s = byId.get(id)
  return s ? toolProcess(s) : ''
}

/** "ETCH-01 — Plasma Etch", the way the assistant names a tool in a sentence. */
export const labelOf = (id: string): string => {
  const p = processOf(id)
  return p ? `${tagOf(id)} · ${p}` : tagOf(id)
}
